// Deep-link query must survive the Auth0 round trip.
//
// Vue Router 4 patches history.replaceState. After handleRedirectCallback
// we used to replace with pathname only, which dropped redirect* from both
// the URL and $route.query. doLogin then saw an empty query and sent a
// cold login to /property-list instead of /ico or /workorder.
//
// sessionStorage is the backup when Auth0 returns to a bare /auth0login
// (allowlisted callback with no extra query). The tab-scoped store is
// written on arrival and again just before loginWithRedirect.

const STORAGE_KEY = "hs_notify_deeplink";

export type DeepLinkQuery = {
  redirecttype?: string;
  redirectcompanyid?: string;
  redirectpropertyid?: string;
  redirectguid?: string;
  propertyid?: string;
  channelId?: string;
};

const KEYS: Array<keyof DeepLinkQuery> = ["redirecttype", "redirectcompanyid", "redirectpropertyid", "redirectguid", "propertyid", "channelId"];

function firstString(value: unknown): string {
  if (Array.isArray(value)) return String(value[0] ?? "").trim();
  if (value == null) return "";
  return String(value).trim();
}

function readKey(source: Record<string, unknown> | URLSearchParams, key: string): string {
  if (source instanceof URLSearchParams) return firstString(source.get(key));
  return firstString(source[key]);
}

export function pickDeepLinkQuery(source: Record<string, unknown> | URLSearchParams | null | undefined): DeepLinkQuery {
  if (!source) return {};
  const out: DeepLinkQuery = {};
  for (const key of KEYS) {
    const value = readKey(source, key);
    if (value) out[key] = value;
  }
  return out;
}

export function hasRedirectParams(query: DeepLinkQuery): boolean {
  return Boolean(query.redirecttype && query.redirectcompanyid);
}

export function resolveDeepLinkQuery(...sources: Array<Record<string, unknown> | URLSearchParams | DeepLinkQuery | null | undefined>): DeepLinkQuery {
  const merged: DeepLinkQuery = {};
  for (const source of sources) {
    const picked = pickDeepLinkQuery(source as Record<string, unknown> | URLSearchParams | null | undefined);
    for (const key of KEYS) {
      if (!merged[key] && picked[key]) merged[key] = picked[key];
    }
  }
  return merged;
}

export function saveDeepLinkQuery(query: DeepLinkQuery): void {
  if (!hasRedirectParams(query) && !(query.propertyid && query.channelId)) return;
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(query));
  } catch {
    /* private mode / quota — URL + $route.query remain the primary source */
  }
}

export function readDeepLinkQuery(): DeepLinkQuery {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    return pickDeepLinkQuery(JSON.parse(raw));
  } catch {
    return {};
  }
}

export function persistDeepLinkFromLocation(routeQuery?: Record<string, unknown>): DeepLinkQuery {
  const query = resolveDeepLinkQuery(new URLSearchParams(window.location.search), routeQuery, readDeepLinkQuery());
  saveDeepLinkQuery(query);
  return query;
}

// Used by the version kill-switch so a deploy reset does not send the
// user to `/?_v=sha` and throw away /login?redirect*.
export function buildReloadUrlKeepingDeepLink(versionSha: string): string {
  persistDeepLinkFromLocation();
  const params = new URLSearchParams(window.location.search);
  params.set("_v", versionSha);
  const path = window.location.pathname || "/";
  const hash = window.location.hash || "";
  return path + "?" + params.toString() + hash;
}

export function clearDeepLinkQuery(): void {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
}
